import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AccountInfo } from '@azure/msal-browser'
import { getAuthService } from '@/msal/index'
import * as Sentry from '@sentry/vue'
import { resetBusinessStores } from '@/utils/businessState'

export const useAuthStore = defineStore('auth', () => {
  // Service MSAL - Utiliser le singleton pour éviter les problèmes d'initialisation
  const authService = getAuthService()
  
  // State
  const userAccount = ref<AccountInfo | undefined>(undefined)
  const isAdmin = ref(false)
  const hasTransparencyRights = ref(true)
  const hasSimulationRights = ref(false)
  const hasSolvencyRights = ref(false)
  // DÉSACTIVÉ - Réactivation future
  // const hasEsgRights = ref(false)
  // const hasRiskRights = ref(false)
  const resetLogin = ref(false)
  
  // Getters
  const isAuthenticated = computed(() => authService.isAuthenticated())
  const accountIdentifier = computed(() => {
    const account = userAccount.value || authService.getAccount()
    return account?.localAccountId || account?.homeAccountId
  })
  
  const hasSavingRights = computed(() => true) // Toujours vrai
  const hasComputingRights = computed(() => 
    hasSolvencyRights.value // ESG et Risk désactivés
  )
  
  const numberModulesShortcuts = computed(() => {
    return [hasSolvencyRights.value] // ESG et Risk désactivés
      .filter(Boolean).length
  })
  
  // Actions
  async function waitForMsalInitialization() {
    await authService.waitForInitialization()
  }
  
  function login() {
    authService.login()
  }
  
  function isInB2CFlow(): boolean {
    // Détecter si nous sommes dans un flux B2C actif (password reset, etc.)
    return authService.isInB2CFlow()
  }
  
  async function logout() {
    // Nettoyer tous les stores avant de déconnecter
    await clearAllStores()
    // Déconnecter via MSAL
    await authService.logout()
  }
  
  /**
   * Nettoie tous les stores et le sessionStorage
   */
  async function clearAllStores() {
    // Reset de l'état local du store auth
    userAccount.value = undefined
    isAdmin.value = false
    hasTransparencyRights.value = true
    hasSimulationRights.value = false
    hasSolvencyRights.value = false
    // DÉSACTIVÉ - Réactivation future
    // hasEsgRights.value = false
    // hasRiskRights.value = false
    resetLogin.value = false
    
    // Nettoyer le contexte Sentry
    Sentry.setUser(null)

    await resetBusinessStores()
  }
  
  function setUserAccount() {
    userAccount.value = authService.getAccount() ?? undefined
    
    // Envoyer le contexte utilisateur à Sentry
    if (userAccount.value) {
      const emailClaim = userAccount.value.idTokenClaims?.email
      Sentry.setUser({
        id: userAccount.value.localAccountId || userAccount.value.homeAccountId,
        username: userAccount.value.username,
        email: typeof emailClaim === 'string' ? emailClaim : undefined,
      })
    }
  }
  
  function setAdmin(value: boolean) {
    isAdmin.value = value
    if (value) {
      // Si admin, tous les droits sont activés
      hasTransparencyRights.value = true
      hasSimulationRights.value = true
      hasSolvencyRights.value = true
      // DÉSACTIVÉ - Réactivation future
      // hasEsgRights.value = true
      // hasRiskRights.value = true
    }
  }
  
  async function checkTransparencyRights(): Promise<boolean> {
    try {
      const result = await authService.isTransparencyMember()
      hasTransparencyRights.value = result
      if (result) {
        setUserAccount()
      }
      return result
    } catch (error) {
      console.error('Erreur vérification droits transparence:', error)
      return false
    }
  }
  
  async function checkSolvencyRights(): Promise<boolean> {
    try {
      const result = await authService.isSolvencyMember()
      hasSolvencyRights.value = isAdmin.value || result
      return result
    } catch (error) {
      console.error('Erreur vérification droits solvabilité:', error)
      return false
    }
  }
  
  async function checkSimulationRights(): Promise<boolean> {
    try {
      const result = await authService.isSimulationMember()
      hasSimulationRights.value = isAdmin.value || result
      return result
    } catch (error) {
      console.error('Erreur vérification droits simulation:', error)
      return false
    }
  }
  
  // DÉSACTIVÉ - Réactivation future
  // async function checkEsgRights(): Promise<boolean> {
  //   try {
  //     const result = await authService.isEsgMember()
  //     hasEsgRights.value = isAdmin.value || result
  //     return result
  //   } catch (error) {
  //     console.error('Erreur vérification droits ESG:', error)
  //     return false
  //   }
  // }
  // 
  // async function checkRiskRights(): Promise<boolean> {
  //   try {
  //     const result = await authService.isRiskMember()
  //     hasRiskRights.value = isAdmin.value || result
  //     return result
  //   } catch (error) {
  //     console.error('Erreur vérification droits risque:', error)
  //     return false
  //   }
  // }
  
  async function checkAdminRights(): Promise<void> {
    const { authenticatedGet } = await import('@/utils/api')
    const { useRootStore } = await import('./root')
    const rootStore = useRootStore()
    
    const account = authService.getAccount()
    if (!account) {
      console.warn('Aucun compte utilisateur trouvé')
      setAdmin(false)
      return
    }
    
    try {
      const response = await authenticatedGet(rootStore.baseUrl + '/api/arago/admin', {
        params: {
          userID: account.localAccountId || account.homeAccountId,
        },
      })
      setAdmin(response.data)
    } catch (error) {
      console.error('Erreur vérification droits admin:', error)
      setAdmin(false)
    }
  }
  
  function reset() {
    userAccount.value = undefined
    isAdmin.value = false
    hasTransparencyRights.value = true
    hasSimulationRights.value = false
    hasSolvencyRights.value = false
    // DÉSACTIVÉ - Réactivation future
    // hasEsgRights.value = false
    // hasRiskRights.value = false
    resetLogin.value = false
  }
  
  return {
    // State
    userAccount,
    isAdmin,
    hasTransparencyRights,
    hasSimulationRights,
    hasSolvencyRights,
    // DÉSACTIVÉ - Réactivation future
    // hasEsgRights,
    // hasRiskRights,
    resetLogin,
    
    // Getters
    isAuthenticated,
    accountIdentifier,
    hasSavingRights,
    hasComputingRights,
    numberModulesShortcuts,
    
    // Actions
    waitForMsalInitialization,
    login,
    logout,
    isInB2CFlow,
    setUserAccount,
    setAdmin,
    checkTransparencyRights,
    checkSolvencyRights,
    checkSimulationRights,
    // DÉSACTIVÉ - Réactivation future
    // checkEsgRights,
    // checkRiskRights,
    checkAdminRights,
    reset,
  }
}, {
  persist: {
    storage: sessionStorage,
  }
})
